import React, { useEffect, useMemo, useState } from 'react';
import { loadStructurePair, getAllPublicStructures, getPublicStructureBySlug, getStructuresByIds } from '../firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Building2, MapPin, ArrowLeft, FileText } from 'lucide-react';
import logger from '../utils/logger';
import { DownloadPassportButton } from '@/features/passport/DownloadPassportButton';

type NavigationSource =
  | 'structure-manager'
  | 'portal-manager'
  | 'travelers-hub'
  | null;

interface AccessibilityPassport2Props {
  onBack: () => void;
  structureSlug?: string | string[] | any;
  navigationSource?: NavigationSource;
  managedStructureIds?: string[];
  branding?: "visit-bergamo" | "default";
}

/**
 * Firestore-only Accessibility Passport:
 * - Private doc: collection "structures"
 * - Public doc: collection "public-structures"
 * - Join key: public.id_backend === private.id (and private.id_public === public.id)
 */
export function AccessibilityPassport2({ onBack, structureSlug, navigationSource, managedStructureIds, branding = "default" }: AccessibilityPassport2Props) {
  const [loading, setLoading] = useState(false);
  const [privateDoc, setPrivateDoc] = useState<any | null>(null);
  const [publicDoc, setPublicDoc] = useState<any | null>(null);
  const [merged, setMerged] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<string>('');

  // Keys to hide from the accessibility passport display
  const HIDDEN_KEYS = new Set<string>([
    "1.1", // Rilevatore
    "2.1", // Email rilevatore
  ]);

  // Labels to hide (normalized for comparison)
  const isHiddenLabel = (label: string): boolean => {
    const normalized = (s: string) => 
      (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    
    const norm = normalized(label);
    return (
      norm.includes("rilevatore") || 
      norm.includes("email rilevatore") ||
      norm.includes("mail rilevatore")
    );
  };

  const getBackButtonText = () => {
    switch (navigationSource) {
      case 'travelers-hub':
        return "Ritorna al Traveler's Hub";
      case 'portal-manager':
        return 'Ritorna alla Portal Dashboard';
      case 'structure-manager':
      default:
        return 'Ritorna alla Dashboard';
    }
  };

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const candidates: string[] = [];
        if (typeof structureSlug === 'string') {
          candidates.push(structureSlug);
        } else if (Array.isArray(structureSlug)) {
          structureSlug.forEach((k) => {
            if (typeof k === 'string') candidates.push(k);
          });
        } else if (structureSlug && typeof structureSlug === 'object') {
          const obj = structureSlug as any;
          if (Array.isArray(obj.managed_structures)) {
            obj.managed_structures.forEach((k: any) => {
              if (typeof k === 'string') candidates.push(k);
            });
          }
          if (Array.isArray(obj.managedStructures)) {
            obj.managedStructures.forEach((k: any) => {
              if (typeof k === 'string') candidates.push(k);
            });
          }
          if (obj.id) candidates.push(String(obj.id));
          if (obj.slug) candidates.push(String(obj.slug));
          if (obj.structure?.id) candidates.push(String(obj.structure.id));
        }
        if (Array.isArray(managedStructureIds)) {
          managedStructureIds.forEach((k) => {
            if (typeof k === 'string') candidates.push(k);
          });
        }
        const uniqueKeys = Array.from(new Set(candidates.map((k) => String(k).trim()).filter(Boolean)));
        if (!uniqueKeys.length) throw new Error('Nessuna struttura selezionata.');

        const publicDocs = await getAllPublicStructures();
        if (structureSlug && typeof structureSlug === 'object') {
          const obj = structureSlug as any;
          void obj;
        }

        // Resolve selected structure first (prefer chosen structure over all managed ones)
        const norm = (s: string) => String(s || '').trim().toLowerCase();
        const selectedKeys = uniqueKeys.slice(0, 1);
        let candidateKeys: string[] = [];

        const resolveFromPublic = (key: string) => {
          const nk = norm(key.replace(/\.json$/i, ''));
          const pubHit = publicDocs.find((d: any) => {
            const ids = [
              String(d?.id || ''),
              String(d?.slug || ''),
              String(d?.id_backend || d?.backend_id || d?.structureId || ''),
            ].map(norm);
            const variants = [nk, `${nk}.json`];
            return variants.some(v => ids.includes(v));
          });
          if (pubHit) {
            const backendId = pubHit.id_backend || pubHit.backend_id || pubHit.structureId;
            return backendId ? String(backendId) : pubHit.id ? String(pubHit.id) : null;
          }
          return null;
        };

        if (selectedKeys.length) {
          const primaryKey = selectedKeys[0];
          const fromPublic = resolveFromPublic(primaryKey);
          if (fromPublic) {
            candidateKeys = [fromPublic];
          } else {
            try {
              const direct = await getStructuresByIds([primaryKey.replace(/\.json$/i, '')]);
              if (Array.isArray(direct) && direct.length && direct[0]?.id) {
                candidateKeys = [String(direct[0].id)];
              }
            } catch (e) {
              logger.warn('[Passport2] getStructuresByIds failed while resolving selected key', e);
            }
          }
        }

        void candidateKeys;

        // If selected could not be resolved, fall back to managed ids (id/id_backend only)
        if (!candidateKeys.length) {
          const expandedSet = new Set<string>();

          // 1) Try matching against structures collection directly (treat keys as private ids)
          const normalizedKeys = uniqueKeys.map((k) => k.replace(/\.json$/i, ''));
          if (normalizedKeys.length) {
            try {
              const directPrivates = await getStructuresByIds(normalizedKeys);
              if (Array.isArray(directPrivates)) {
                directPrivates.forEach((doc: any) => {
                  if (doc?.id) expandedSet.add(String(doc.id));
                });
              }
            } catch (e) {
              logger.warn('[Passport2] getStructuresByIds failed while expanding keys', e);
            }
          }

          // 2) Try matching against public-structures (id/slug -> id_backend)
          if (Array.isArray(publicDocs) && publicDocs.length) {
            for (const key of uniqueKeys) {
              const nk = norm(key.replace(/\.json$/i, ''));
              const pubHit = publicDocs.find((d: any) => {
                const ids = [
                  String(d?.id || ''),
                  String(d?.slug || ''),
                  String(d?.id_backend || d?.backend_id || d?.structureId || ''),
                ].map(norm);
                const variants = [nk, `${nk}.json`];
                return variants.some(v => ids.includes(v));
              });
              if (pubHit) {
                if (pubHit.id_backend || pubHit.backend_id || pubHit.structureId) {
                  expandedSet.add(String(pubHit.id_backend || pubHit.backend_id || pubHit.structureId));
                } else if (pubHit.id) {
                  expandedSet.add(String(pubHit.id));
                }
              }
            }
          }

          candidateKeys = Array.from(expandedSet).map((k) => k.replace(/\.json$/i, ''));
          if (!candidateKeys.length) {
            throw new Error('Nessuna struttura selezionata.');
          }
        }
        let effectivePrivate: any = null;
        let effectivePublic: any = null;
        let matchedKey = '';

        for (const rawKey of candidateKeys) {
          const normKey = String(rawKey).replace(/\.json$/i, '');
          const normKeyLower = normKey.toLowerCase();
          // Try matching directly in public collection by id or id_backend
          if (Array.isArray(publicDocs) && publicDocs.length) {
            const pubMatch = publicDocs.find((d: any) => {
              const idMatch = String(d?.id || '').toLowerCase() === normKeyLower;
              const backendMatch = String(d?.id_backend || d?.backend_id || d?.structureId || '').toLowerCase() === normKeyLower;
              const slugMatch = String(d?.slug || '').toLowerCase() === normKeyLower;
              return idMatch || backendMatch || slugMatch;
            });
            if (pubMatch) {
              effectivePublic = effectivePublic || pubMatch;
              const backendId = pubMatch.id_backend || pubMatch.backend_id || pubMatch.structureId;
              if (!effectivePrivate && backendId) {
                try {
                  const pairFromBackend = await loadStructurePair(backendId);
                  if (pairFromBackend?.privateDoc) {
                    effectivePrivate = pairFromBackend.privateDoc;
                  }
                } catch (e) {
                  logger.warn('[Passport2] loadStructurePair via public.id_backend failed', e);
                }
              }
              matchedKey = normKey;
              if (effectivePrivate || effectivePublic) break;
            }
          }

          let pair: { privateDoc: any; publicDoc: any } = { privateDoc: null, publicDoc: null };
          try {
            pair = await loadStructurePair(normKey);
          } catch (e) {
            logger.warn('[Passport2] loadStructurePair failed for candidate', normKey, e);
          }

          effectivePrivate = pair.privateDoc || effectivePrivate;
          effectivePublic = pair.publicDoc || effectivePublic;

          if (!effectivePublic) {
            try {
              const direct = await getPublicStructureBySlug(normKey);
              if (direct) effectivePublic = direct;
            } catch (e) {
              logger.warn('[Passport2] getPublicStructureBySlug failed', e);
            }
          }

          if (effectivePrivate || effectivePublic) {
            matchedKey = normKey;
            break;
          }
        }
        if (!effectivePrivate && !effectivePublic) {
        }

        if (!active) return;
        setPrivateDoc(effectivePrivate);
        setPublicDoc(effectivePublic);

        const publicModules = Array.isArray(effectivePublic?.modules) ? effectivePublic.modules : [];
        const privateModules = Array.isArray(effectivePrivate?.modules) ? effectivePrivate.modules : [];
        const mergedModules = publicModules.length ? publicModules : privateModules;

        const mergedPhotos = Array.isArray(effectivePublic?.photos)
          ? effectivePublic.photos
          : Array.isArray(effectivePrivate?.photos)
            ? effectivePrivate.photos
            : [];

        const mergedObj = (effectivePublic || effectivePrivate)
          ? {
              ...(effectivePrivate || effectivePublic || {}),
              modules: mergedModules,
              public_infos: effectivePublic?.public_infos ?? effectivePrivate?.public_infos,
              photos: mergedPhotos,
            }
          : null;

        setMerged(mergedObj);
        logger.debug('[Passport2] loaded', {
          key: matchedKey,
          hasPrivate: !!effectivePrivate,
          hasPublic: !!effectivePublic,
          modules: mergedModules.map((m: any) => m?.type || m?.name || m?.module_name || m?.module_id).filter(Boolean),
        });
      } catch (e: any) {
        if (!active) return;
        setError(e?.message || 'Errore nel caricamento dei dati da Firestore.');
        setMerged(null);
        setPrivateDoc(null);
        setPublicDoc(null);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [structureSlug]);

  const data = merged;

  const pdfStructure = useMemo(() => {
    const modules = Array.isArray(data?.modules) ? data.modules : [];
    const mappedModules = modules.map((mod: any) => ({
      name: String(mod?.name || mod?.module_name || mod?.type || 'Modulo'),
      type: String(mod?.type || mod?.name || mod?.module_name || 'Modulo'),
      content: (mod?.content || mod?.questions || {}) as Record<string, string>,
    }));

    return {
      id_backend: String(
        data?.id_backend || data?.backend_id || data?.structureId || data?.id || ''
      ),
      lastModified:
        data?.lastModified ||
        data?.last_modified ||
        data?.updated_at ||
        data?.updatedAt,
      public_infos: data?.public_infos,
      modules: mappedModules,
    };
  }, [data]);

  const generalInfo = useMemo(() => {
    const acc = data?.structure?.accommodation_data || {};
    const contacts = acc.contacts || {};
    const address = acc.address || {};
    const firstModule = Array.isArray(data?.modules) ? data.modules[0] : null;
    const moduleContent = firstModule?.content || firstModule?.questions || {};
    const extractValue = (val: any) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object' && 'value' in val) return String((val as any).value ?? '');
      return String(val ?? '');
    };
    const moduleName =
      extractValue(moduleContent['6.1']) ||
      extractValue(moduleContent['NOME STRUTTURA']) ||
      extractValue(moduleContent['Nome struttura']) ||
      extractValue(moduleContent['nome struttura']) ||
      extractValue(moduleContent['nome']);

    return {
      name:
        moduleName ||
        acc.accommodation_name ||
        publicDoc?.structure?.accommodation_data?.accommodation_name ||
        publicDoc?.name ||
        data?.name ||
        '',
      type: acc.type || publicDoc?.structure?.accommodation_data?.type || data?.type || '',
      address:
        address.street ||
        publicDoc?.structure?.accommodation_data?.address?.street ||
        '',
      city: address.city || publicDoc?.structure?.accommodation_data?.address?.city || '',
      phone: contacts.phone || '',
      email: contacts.email || '',
      website: contacts.website || '',
    };
  }, [data, publicDoc]);

  const modulesList = useMemo(() => {
    const mods = Array.isArray(data?.modules) ? data.modules : [];
    const normalizeModuleTitle = (raw: string) => {
      if (!raw) return raw;
      const t = String(raw).trim().toLowerCase();
      if (t === 'camere non attrezzate per d' || t === 'camere non attrezzate per disabilita') {
        return 'Camere non attrezzate per disabilità';
      }
      if (t === 'scheda ascensore') {
        return 'Scheda ascensore';
      }
      if (t === 'supporti e servizi per pers' || t === 'supporti e servizi per persone con disabilita') {
        return 'Supporti e servizi per persone con disabilità';
      }
      return raw;
    };

    const preferredOrder = [
      'dati info azienda',
      'parcheggi',
      'mezzi pubblici',
      'ingresso',
      'accoglienza e servizi utili',
      'supporti e servizi per persone con disabilità',
      'segnaletica di orientamento',
      'mobilità verticale',
      'mobilità orizzontale',
      'scheda ascensore',
      'scheda servizi igienici',
      'uscite di sicurezza',
      'camere attrezzate per disab',
      'camere non attrezzate per disabilità',
      'sala colazioni',
      'scheda bar',
      'scheda ristorante',
    ].map((s) => s.toLowerCase());

    const scored = mods.map((m: any, idx: number) => {
      const rawTitle = m?.type || m?.name || m?.module_name || 'Modulo';
      const title = normalizeModuleTitle(rawTitle);
      const lower = String(title || '').trim().toLowerCase();
      const orderIndex = preferredOrder.indexOf(lower);
      return {
        key: `${rawTitle || 'mod'}-${idx}`,
        title,
        content: m?.content || m?.questions || {},
        description: m?.description,
        order: orderIndex === -1 ? 999 + idx : orderIndex,
      };
    });

    return scored
      .filter((m) => Object.values(m.content || {}).some((v) => v !== null && v !== undefined && String(v).trim() !== ''))
      .sort((a, b) => a.order - b.order);
  }, [data]);

  useEffect(() => {
    // Default to first module when available
    if (!activeItem && modulesList.length > 0) {
      setActiveItem(modulesList[0].key);
    }
  }, [modulesList]);

  const selectedModule = useMemo(() => {
    return modulesList.find((m) => m.key === activeItem) || modulesList[0] || null;
  }, [modulesList, activeItem]);

  const isUrl = (val: any) => {
    if (typeof val !== 'string') return false;
    return /^(https?:\/\/|www\.)/i.test(val.trim());
  };

  const normalizeUrl = (val: string) => {
    const s = val.trim();
    return s.match(/^https?:\/\//i) ? s : `http://${s}`;
  };

  // URL field labels (case/accent insensitive)
  const URL_LABELS = new Set<string>([
    'sito web',
    'indicare il link a maps',
    'se si indicare i link',
    'link maps parcheggi nelle vicinanze della struttura',
  ]);

  // Normalize label: lowercase + remove accents
  const normalizeLabel = (raw: string): string => {
    return (raw || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Check if a label identifies a URL field
  const isUrlLabel = (label: string): boolean => {
    const normalizedLabel = normalizeLabel(label);
    
    // First check exact whitelist match
    if (URL_LABELS.has(normalizedLabel)) {
      return true;
    }
    
    // Robust heuristics for label matching
    if (normalizedLabel.includes('indicare') && normalizedLabel.includes('link')) {
      return true;
    }
    if (normalizedLabel.includes('maps')) {
      return true;
    }
    if (normalizedLabel.includes('sito') && normalizedLabel.includes('web')) {
      return true;
    }
    
    return false;
  };

  // Split possible links by common separators
  const splitPossibleLinks = (text: string): string[] =>
    text
      .split(/\s+|,\s*|;\s*|\n+/g)
      .map(t => t.trim())
      .filter(Boolean);

  // Check if a string looks like a URL
  const isHttpLike = (s: string): boolean =>
    /^https?:\/\//i.test(s) || /^www\./i.test(s);

  // Render URL value with support for multiple links
  // Convert full URL to human-readable anchor text
  const prettyLinkText = (fullUrl: string): string => {
    try {
      // Parse the URL safely
      const url = new URL(fullUrl);
      
      // Check if it's a Google Maps URL
      if (url.hostname.includes('maps.google') || url.hostname.includes('google.com/maps')) {
        // Try to extract place name from path or query params
        // Maps URLs typically have /place/{placeName} or place parameter
        const pathMatch = url.pathname.match(/\/place\/([^/]+)/);
        if (pathMatch && pathMatch[1]) {
          let placeName = decodeURIComponent(pathMatch[1]).replace(/\+/g, ' ');
          return `Apri su Google Maps: ${placeName}`;
        }
        // Fallback for Maps URLs without clear place name
        return 'Apri su Google Maps';
      }
      
      // For other URLs: show domain + shortened path
      let label = url.hostname;
      if (url.pathname && url.pathname !== '/') {
        const pathParts = url.pathname.split('/').filter(p => p);
        if (pathParts.length > 0) {
          label += '/' + pathParts.slice(0, 2).join('/');
          if (pathParts.length > 2) {
            label += '…';
          }
        }
      }
      return label;
    } catch (e) {
      // Fallback: truncate the original URL
      return fullUrl.length > 50 ? fullUrl.substring(0, 47) + '…' : fullUrl;
    }
  };

  const renderUrlValue = (rawValue: any): React.ReactNode => {
    const text = String(rawValue ?? '').trim();
    if (!text) return '';

    // Regex to match URLs: http(s):// or www.
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts: Array<{ type: 'text' | 'url'; content: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
      // Add any text before this URL
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      // Add the URL
      parts.push({ type: 'url', content: match[0] });
      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }

    // If no URLs found, render as plain text
    if (parts.length === 0 || parts.every(p => p.type === 'text')) {
      return text;
    }

    // Render parts with links
    return parts.map((part, i) => {
      if (part.type === 'url') {
        const href = normalizeUrl(part.content);
        return (
          <a
            key={`url-${i}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {prettyLinkText(href)}
          </a>
        );
      } else {
        return <React.Fragment key={`text-${i}`}>{part.content}</React.Fragment>;
      }
    });
  };

  const renderValue = (val: any) => {
    if (val == null) return <span className="text-s text-muted-foreground">-</span>;
    if (isUrl(val)) {
      const href = normalizeUrl(String(val));
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-4"
        >
          {String(val)}
        </a>
      );
    }
    if (typeof val === 'object') {
      return (
        <pre className="whitespace-pre-wrap text-s text-muted-foreground">
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }
    return <span className="text-m font-semibold leading-relaxed">{String(val)}</span>;
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Caricamento passaporto...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-red-600 text-s">{error}</p>
          <Button variant="outline" onClick={onBack}>Indietro</Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground text-s">Nessun dato trovato in Firestore.</p>
          <Button variant="outline" onClick={onBack}>Indietro</Button>
        </div>
      </div>
    );
  }

  const portalName = data?.structure?.portal_administrators?.portal_name || "";
  const isVisitBergamo = branding === "visit-bergamo";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navbar reused from app style */}
      <header
        className={isVisitBergamo ? "p-4 shadow-lg border-b" : "border-b border-blue-200/30 px-4 py-3 shadow-lg"}
        style={isVisitBergamo ? { backgroundColor: "#0f0f0f", borderColor: "#1c1c1c" } : { backgroundColor: "#003c5d" }}
      >
        <div className={`max-w-7xl mx-auto ${isVisitBergamo ? "space-y-3" : "space-y-2"}`}>
          <div className="flex justify-center">
            <img
              src={isVisitBergamo ? "/visit-bergamo-logo.svg" : "/travelin-logo-dark.svg"}
              alt={isVisitBergamo ? "Visit Bergamo" : "Travelin"}
              className={isVisitBergamo ? "h-14 w-auto object-contain" : "h-9 w-auto object-contain"}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={onBack}
                className={isVisitBergamo ? "border-white/20 text-white bg-white/10" : "bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 hover:border-white"}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {getBackButtonText()}
              </Button>
              {/* PDF download for public structure */}
              <DownloadPassportButton
                publicStructure={pdfStructure}
                structureName={generalInfo.name || 'Struttura'}
                variant="outline"
              />
            </div>

            <div className="text-white">
                <h1 className="text-l font-medium">Accessibility Passport</h1>
                <p className={`text-s ${isVisitBergamo ? "text-white" : "text-orange-100"}`}>{generalInfo.name || 'Struttura'}</p>
                <div className="mt-1 flex items-center gap-2 text-ss text-white/80">
                  <span>Powered by</span>
                  <img
                  src="/travelin-logo.svg"
                    alt="Travelin"
                    className="h-6 w-auto rounded-sm bg-white px-2 py-0.5"
                  />
                </div>
              </div>
          </div>
        </div>
      </header>

      <div className="flex-1 p-4 min-h-0 w-full">
        <div className="h-full max-w-5xl mx-auto flex flex-col">
          

          <Card className="mb-4">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-start gap-3">
               
                <div className="flex-1">
                  <h1 className="text-xxl font-semibold">{generalInfo.name || 'Struttura'}</h1>
                  <div className="text-s text-muted-foreground flex gap-3 flex-wrap">
                    {generalInfo.type && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" /> {generalInfo.type}
                      </span>
                    )}
                    {generalInfo.address && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {generalInfo.address}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
{/* GENERAL INFO */}
          <Card className="mb-4 bg-gradient-to-r from-blue-50 to-white border-blue-100">
            <CardContent className="p-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold">Informazioni Utili</h2>
                  {data?.public_infos?.paragraphs && (
                    <div className="space-y-2">
                      <div className="space-y-1 text-s leading-relaxed text-foreground">
                        {Object.entries(data.public_infos.paragraphs).map(([k, v]) => (
                          <p key={k}>
                            <span className="font-semibold">{k}:</span>{' '}
                            <span>{String(v)}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          

          <div className="flex flex-row gap-4 flex-1 w-full">
            <Card className="w-64 min-w-64 max-w-64 flex-shrink-0">
              <CardHeader className="pb-3">
                <CardTitle>Navigation</CardTitle>
                <CardDescription>Seleziona un modulo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {modulesList.length === 0 && (
                  <p className="text-s text-muted-foreground">Nessun modulo disponibile.</p>
                )}
                {modulesList.map((mod) => (
                  <button
                    type="button"
                    key={mod.key}
                    onClick={() => setActiveItem(mod.key)}
                    className={`w-full text-left px-3 py-2 rounded-md border transition hover:bg-muted ${activeItem === mod.key ? 'border-primary bg-primary/5 text-primary' : 'border-border'}`}
                  >
                    {mod.title}
                  </button>
                ))}
              </CardContent>
            </Card>

            <div className="flex-1 space-y-4 overflow-y-auto min-w-0">
              {modulesList.length === 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <p className="text-s text-muted-foreground">Nessun contenuto da mostrare.</p>
                  </CardContent>
                </Card>
              ) : (
                (() => {
                  const mod = modulesList.find((m) => m.key === activeItem) || modulesList[0];
                  return (
                    <Card key={mod.key}>
                      <CardHeader>
                        <CardTitle>{mod.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {Object.entries(mod.content).length === 0 ? (
                          <p className="text-s text-muted-foreground">Nessun contenuto.</p>
                        ) : (
                          <div className="space-y-3">
                            {Object.entries(mod.content)
                              .filter(([k, v]) => {
                                // Skip hidden keys
                                if (HIDDEN_KEYS.has(k)) return false;
                                // Skip hidden labels
                                if (isHiddenLabel(k)) return false;
                                return true;
                              })
                              .map(([k, v]) => (
                                <div key={k} className="space-y-1 rounded-md bg-muted/40 p-3">
                                  <div className="text-m font-semibold" style={{ color: '#64748b' }}>{k}</div>
                                  <div className="text-l text-black font-semibold whitespace-pre-wrap break-words">{isUrlLabel(k) ? renderUrlValue(v) : String(v ?? '')}</div>
                                </div>
                              ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessibilityPassport2;
